/* globals angular, requirejs, _ */

import extend from 'lodash/extend';
import bind from 'lodash/bind';

angular.module('atex.aceui.ui').factory('WidgetService',
  ['$q', '$timeout', 'FileDeliveryService', 'ContentService',

    function ($q, $timeout, FileDeliveryService, ContentService) {
      var widgetDefinitionLoadCache = {};
      var widgetAliasResolveCache = {};

      var WIDGET_OBJECT_EXTERNAL_ID_PREFIX = '_widget/';

      var factoryRunner = function (widgetType, widgetName, widgetRequires, widgetFactoryFn) {
        var deferred = $q.defer();

        requirejs (widgetRequires, function () {
          try {
            var widgetFactoryFnOutput = widgetFactoryFn.apply(this, arguments);
            deferred.resolve({ type: widgetType, name: widgetName, artifact: widgetFactoryFnOutput });
          } catch (error) {
            var e = atex.aceui.error.Error({
              origin: 'atex.aceui.ui.WidgetService',
              type: atex.aceui.error.types.UNKNOWN,

              rootCause: error,
              cause: 'Error loading requirements for widget \'' + widgetName + '\'!'
            });

            deferred.reject(e);
          }
        }, function (error) {
          var e = atex.aceui.error.Error({
            origin: 'atex.aceui.ui.WidgetService',
            type: atex.aceui.error.types.UNKNOWN,

            rootCause: error,
            cause: 'Error loading requirements for widget \'' + widgetName + '\'!'
          });

          deferred.reject(e);
        });

        return deferred.promise;
      };

      var register = function (widgetType, widgetName, widgetRequires, widgetFactoryFn) {
        var widgetDefinition = widgetDefinitionLoadCache[widgetName];

        if (typeof widgetDefinition === 'undefined') {
        // This widget does not exist, and nobody is waiting for it either

          widgetDefinitionLoadCache[widgetName] = factoryRunner(widgetType, widgetName, widgetRequires, widgetFactoryFn);
        } else if (typeof widgetDefinition.resolve !== 'undefined') {
        // This widget does not exist yet, but someone is waiting for it

          widgetDefinitionLoadCache[widgetName] = factoryRunner(widgetType, widgetName, widgetRequires, widgetFactoryFn);
          widgetDefinition.resolve(widgetDefinitionLoadCache[widgetName]);
        }
      };

      var handleRequires = function (requires, baseUrl) {
        var paths = {};

        requires.forEach(function (requirement) {
          if (!requirejs.defined(requirement['name'])) {
            var pathName = requirement['path'].replace(/\.([^.]*).$/, ''); // Strip file name extensions

            // Everything except protocol://filePath and /filePath should be
            // relative to the given base URL (the content URL in the File Service)

            if (!pathName.match(/.*:\/\//) && !pathName.match(/^[/]/)) {
              pathName = baseUrl + '/' + pathName;
            }

            paths[requirement['name']] = pathName;
          }
        });

        requirejs({
          paths: paths
        });
      };

      var loadWidget = function (widgetName, widgetBundleId) {
        var contentId = WIDGET_OBJECT_EXTERNAL_ID_PREFIX + widgetBundleId;

        return ContentService.getContent({ alias: contentId, view: 'aceLatest' }).then(function (result) {
          widgetAliasResolveCache[widgetName] = result.content.system.version;

          return FileDeliveryService.getContentVersionBaseUrl(result.content.system.version)
            .then(function (baseUrl) {
              return FileDeliveryService.getTextFile({
                version: result.content.system.version,
                fileName: 'manifest.json'
              })
                .then(function (fileResult) {
                  var manifestJSON = fileResult.text;
                  return JSON.parse(manifestJSON);
                }, function (error) {
                // An error of type NOT_FOUND means we can at least try to fallback to widget.js

                  if (typeof error === 'undefined' || typeof error.type === 'undefined' || error.type !== atex.aceui.error.types.NOT_FOUND) {
                    return $q.reject(error);
                  }

                  return {name: widgetName, scripts: 'widget.js'};
                })
                .then(function (widgetManifest) {
                  var scriptFileNames = ['widget.js'], scriptFilePromises = [];

                  if (typeof widgetManifest !== 'undefined' && typeof widgetManifest.scripts !== 'undefined') {
                    if (Array.isArray(widgetManifest.scripts)) {
                      scriptFileNames = widgetManifest.scripts;
                    } else if (typeof widgetManifest.scripts === 'string') {
                      scriptFileNames = [widgetManifest.scripts];
                    } else if (typeof widgetManifest.scripts[widgetName] !== 'undefined') {
                      scriptFileNames = Array.isArray(widgetManifest.scripts[widgetName]) ? widgetManifest.scripts[widgetName] : [widgetManifest.scripts[widgetName]];
                    }
                  }

                  handleRequires(widgetManifest.requires || [], baseUrl);

                  scriptFilePromises = scriptFileNames.map(function (scriptFileName) {
                    return FileDeliveryService.getTextFile({
                    // alias: contentId,
                      version: result.content.system.version,
                      fileName: scriptFileName
                    });
                  });

                  return $q.all(scriptFilePromises);
                })
                .then(function (scriptFiles) {
                  var sharedThis = {};
                  var scopedAtex = extend({}, atex);

                  scopedAtex.aceui = extend({}, scopedAtex.aceui);

                  scopedAtex.aceui.baseUrl = baseUrl;
                  scopedAtex.aceui.register = bind(register, {});

                  for (var i = 0; i < scriptFiles.length; i++) {
                    var scriptFileCode = scriptFiles[i].text;

                    try {
                      var epilogue = '\n//# sourceURL=' + widgetName;
                      var codeRunner = new Function('window', 'atex', '"use strict";\n\n' + scriptFileCode + epilogue);
                      codeRunner.call(sharedThis, window, scopedAtex);
                    } catch (error) {
                      var e = atex.aceui.error.Error({
                        origin: 'atex.aceui.ui.WidgetService',
                        type: atex.aceui.error.types.UNKNOWN,

                        rootCause: error,
                        cause: 'Error while loading widget \'' + widgetName + '\'!'
                      });

                      widgetDefinitionLoadCache[widgetName].reject(e);

                      return $q.reject(e);
                    }
                  }
                });
            });
        })
          .then(null, function (error) {
            var widgetLoadObject = widgetDefinitionLoadCache[widgetName];

            if (typeof widgetLoadObject !== 'undefined') {
              widgetDefinitionLoadCache[widgetName].reject(error);
            }
          });
      };

      var getWidgetDefinition = function (widgetName, widgetBundleId) {
        var widgetDefinition = widgetDefinitionLoadCache[widgetName];

        if (typeof widgetDefinition === 'undefined') {
        // This is the first time someone requests a definition for this widget, so we put a
        // deferred object in the load cache. We then begin to asynchronously load it.

          widgetDefinitionLoadCache[widgetName] = $q.defer();

          loadWidget(widgetName, widgetBundleId);

          $timeout(function () {
            var widgetDefinitionLoadObject = widgetDefinitionLoadCache[widgetName];

            if (typeof widgetDefinitionLoadObject !== 'undefined' && typeof widgetDefinitionLoadObject.resolve !== 'undefined') {
              delete widgetDefinitionLoadCache[widgetName];

              var error = atex.aceui.error.Error({
                origin: 'atex.aceui.ui.WidgetService',
                type: atex.aceui.error.types.TIMEOUT,

                cause: 'Timeout while loading widget \'' + widgetName + '\'!'
              });

              widgetDefinitionLoadObject.reject(error);
            }
          }, 30000);

          return widgetDefinitionLoadCache[widgetName].promise;
        } else if (typeof widgetDefinition.resolve !== 'undefined') {
        // this widget does not exist, but someone else has also requested it (pending).

          return widgetDefinition.promise;
        } else {
        // The widget is loaded (and widgetDefinition is a promise)
          return widgetDefinition;
        }
      };

      var registerAngularDirectiveFor = function (widgetDefinition) {
        widgetDefinition.directiveName = widgetDefinition.name.replace(/\./g,'');
        angular.module('atex.aceui.ui.widgets').registerDirective(widgetDefinition.directiveName, widgetDefinition.artifact);
      };

      return {
        getWidget: function (widgetName, widgetBundleId) {
          widgetBundleId = widgetBundleId || widgetName;

          return getWidgetDefinition(widgetName, widgetBundleId)
            .then(function (widgetDefinition) {
              // We only support ng-directive at the moment

              if (widgetDefinition.type !== 'ng-directive') {
                var error = atex.aceui.error.Error({
                  origin: 'atex.aceui.ui.WidgetService',
                  type: atex.aceui.error.types.INVALID_PARAMETERS,

                  cause: 'Unrecognized widget type \'' + widgetDefinition.type + '\'!'
                });

                return $q.reject(error);
              }

              if (typeof widgetDefinition.directiveName === 'undefined') {
                registerAngularDirectiveFor(widgetDefinition);
              }

              return { type: widgetDefinition.type, directiveName: widgetDefinition.directiveName, widgetContentVersion: widgetAliasResolveCache[widgetName] };
            });
        }
      };
    }
  ]);
